import { STATUS_CODE_CANCEL } from "constant/app";
import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import React from "react";

const _formErrorsInitialState = {
  errors: false,
  formErrors: {},
  systemErrors: {},
};

function getErrorMessage(resp) {
  if (!resp) {
    return "";
  }

  if (resp instanceof Error) {
    return `${resp.name}: ${resp.message}`;
  }

  const { status } = resp;

  if (status === 200) {
    return "";
  }

  if (status === STATUS_CODE_CANCEL) {
    return "Request cancelled.";
  }

  const message = get(resp, "data.message", {});
  const msgType = Array.isArray(message) ? "array" : typeof message;

  switch (msgType) {
    case "string":
      return message;
    case "array":
      return "<li>" + message.join("</li><li>") + "</li>";
    case "object": {
      const lines = Object.entries(message).map(([k, v]) => `${k}: ${v}`);

      if (lines.length > 1) {
        return "<li>" + lines.join("</li><li>") + "</li>";
      }

      if (lines.length) {
        return lines[0];
      }
      break;
    }
    default:
      break;
  }

  const statusMsg = status ? ` (${status})` : "";

  return `Failed to complete the request. Please try again.${statusMsg}`;
}

/*
 * This function allows us to easily map api responses to
 * ui component form validation. You can use the return value to directly
 * set component state and update form validation status accordingly.
 */
function getFormErrors(resp = {}) {
  const { status, data = {} } = resp;
  const errorState = cloneDeep(_formErrorsInitialState);

  if (!status) {
    return errorState;
  }

  if (status < 400) {
    return errorState;
  }

  errorState.errors = true;
  let { message } = data;
  // Make sure form/system errors is always an object.
  if (typeof message === "string") {
    message = { message };
  }

  if (status >= 500) {
    errorState.systemErrors = message;
  } else {
    errorState.formErrors = message;
  }

  return errorState;
}

function getErrorDetails(resp, key = "extra") {
  if (!resp || resp.status === 200) {
    return "";
  }

  if (resp.status === STATUS_CODE_CANCEL) {
    return "Request cancelled.";
  }

  let elemExtra = null;
  try {
    const extra = resp.data[key];

    if (!extra) {
      return null;
    }

    switch (typeof extra) {
      case "string":
        elemExtra = <li>{extra}</li>;
        break;
      case "object":
        elemExtra = [];
        if (Array.isArray(extra)) {
          elemExtra = extra.map((line, idx) => {
            return <li key={`err-detail-${idx}`}>{line}</li>;
          });
        } else {
          for (const [k, v] of Object.entries(extra)) {
            elemExtra.push(<li key={k}>{`${k}: ${v}`}</li>);
          }
        }
        break;
      default:
        throw new TypeError(typeof extra);
    }
  } catch (e) {
    console.error("Failed to parse error details.", e);
  }

  return elemExtra ? <ul>{elemExtra}</ul> : elemExtra;
}

export { getErrorDetails, getErrorMessage, getFormErrors };
